import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

export default function TermsPage() {
  return (
    <div className="min-h-screen pt-28 pb-20">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-[#a1a1aa] text-sm mb-8">
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-white">Terms of Use</span>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Terms of <span className="text-[#dc2626]">Use</span>
          </h1>
          <p className="text-[#71717a] text-sm mb-12">Last updated: 01 January 2026</p>

          <div className="prose prose-invert max-w-none space-y-8">
            <section>
              <h2 className="text-xl font-semibold text-white mb-4">Introduction</h2>
              <p className="text-[#a1a1aa] leading-relaxed">
                Welcome to the Cube Multimedia website (the "www.cubemultimedia.in"). By accessing or using the Website, you agree to comply with and be bound by these Terms of Use. If you do not agree to these Terms of Use, please do not use the Website.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">Changes to Terms</h2>
              <p className="text-[#a1a1aa] leading-relaxed">
                Cube Multimedia reserves the right to modify, amend, or update these Terms of Use at any time without prior notice. Your continued use of the Website following any changes constitutes your acceptance of the modified Terms of Use.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">Use of the Website</h2>
              
              <h3 className="text-lg font-medium text-white mb-3">Content</h3>
              <p className="text-[#a1a1aa] leading-relaxed mb-4">
                All content provided on the Website, including text, images, graphics, videos, and other materials, is for informational purposes only and is subject to change without notice.
              </p>

              <h3 className="text-lg font-medium text-white mb-3">User Conduct</h3>
              <p className="text-[#a1a1aa] leading-relaxed mb-4">
                You agree to use the Website for lawful purposes and in a manner consistent with all applicable local, state, and federal laws and regulations. You will not engage in any activity that could harm the Website, other users, or Cube Multimedia.
              </p>

              <h3 className="text-lg font-medium text-white mb-3">User Registration</h3>
              <p className="text-[#a1a1aa] leading-relaxed mb-4">
                Some areas of the Website may require user registration. You are responsible for maintaining the confidentiality of your account information and password. Cube Multimedia reserves the right to terminate or suspend your account if you provide inaccurate or incomplete information or violate these Terms of Use.
              </p>

              <h3 className="text-lg font-medium text-white mb-3">Intellectual Property</h3>
              <p className="text-[#a1a1aa] leading-relaxed mb-4">
                The content on the Website is protected by copyright, trademark, and other intellectual property laws. You may not reproduce, modify, distribute, or republish any content from this Website without prior written consent from Cube Multimedia.
              </p>

              <h3 className="text-lg font-medium text-white mb-3">Third-Party Links</h3>
              <p className="text-[#a1a1aa] leading-relaxed">
                The Website may contain links to third-party websites. Cube Multimedia does not endorse or control the content of these websites and is not responsible for their content or practices.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">Sale of Digital Services</h2>
              
              <h3 className="text-lg font-medium text-white mb-3">Service Orders</h3>
              <p className="text-[#a1a1aa] leading-relaxed mb-4">
                When you place an order for our digital services, you agree to provide accurate and complete information about yourself, including your contact information and payment details. Cube Multimedia reserves the right to decline or cancel orders if we suspect fraudulent activity or if payment is not received.
              </p>

              <h3 className="text-lg font-medium text-white mb-3">Payment</h3>
              <p className="text-[#a1a1aa] leading-relaxed mb-4">
                Payment for digital services is due at the time of order placement. Cube Multimedia may offer various payment methods, and you agree to comply with the terms and conditions associated with your chosen payment method.
              </p>

              <h3 className="text-lg font-medium text-white mb-3">Service Delivery</h3>
              <p className="text-[#a1a1aa] leading-relaxed mb-4">
                Upon successful payment, you will receive access to or delivery of the purchased digital service in the manner specified during the ordering process. Cube Multimedia will make reasonable efforts to provide access promptly, but does not guarantee uninterrupted or error-free access.
              </p>

              <h3 className="text-lg font-medium text-white mb-3">Intellectual Property</h3>
              <p className="text-[#a1a1aa] leading-relaxed mb-4">
                The purchase of digital services does not transfer any intellectual property rights. You may use the services only for personal or business purposes as outlined in the service description.
              </p>

              <h3 className="text-lg font-medium text-white mb-3">No Refunds</h3>
              <p className="text-[#a1a1aa] leading-relaxed mb-4">
                Cube Multimedia does not offer refunds for digital services. Once a purchase is made and access to the digital service is granted, no refunds will be provided.
              </p>

              <h3 className="text-lg font-medium text-white mb-3">Service Changes</h3>
              <p className="text-[#a1a1aa] leading-relaxed">
                Cube Multimedia reserves the right to modify, update, or discontinue any digital service without prior notice. If such changes significantly affect your access or use of a purchased service, you may be eligible for a prorated credit toward future services, at the sole discretion of Cube Multimedia.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">Disclaimer of Warranties</h2>
              <p className="text-[#a1a1aa] leading-relaxed">
                The use of the Website is at your own risk. Cube Multimedia makes no warranties or representations regarding the accuracy, completeness, or suitability of the content on the Website. The Website is provided on an "as-is" and "as-available" basis without warranties of any kind.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">Limitation of Liability</h2>
              <p className="text-[#a1a1aa] leading-relaxed">
                Cube Multimedia, its affiliates, directors, officers, employees, or agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, revenues, data, goodwill, or other intangible losses, arising from your use or inability to use the Website, unauthorized access to our systems, interruptions, bugs, viruses, or errors in content, whether based on warranty, contract, tort, or any other legal theory, even if Cube Multimedia has been advised of the possibility of such damages.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">Contact Information</h2>
              <p className="text-[#a1a1aa] leading-relaxed">
                If you have any questions or concerns regarding these Terms of Use, please contact us at{" "}
                <a href="mailto:contact@cubemultimedia.in" className="text-[#dc2626] hover:underline">
                  contact@cubemultimedia.in
                </a>
              </p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
